import { MockAuth0Client } from "@/auth/auth0/mockAuth0Client";
import useAws from "@/aws/useAws";
import useAppConfig from "@/composables/useAppConfig";
import { useCookies } from "@/composables/useCookies";
import useEvents from "@/composables/useEvents";
import useOnlineStatus from "@/composables/useOnlineStatus";
import useUserSettings from "@/composables/useUserSettings";
import useWebNotifications from "@/composables/useWebNotifications";
import { initialAbility } from "@/libs/acl/config";
import axios from "@/libs/axios";
import axiosApiGateway from "@/libs/axiosV1";
import router from "@/router";
import store from "@/store";
import { isMobile, isPwa } from "@/utils/mobile";
import { getRootDomain, isLocalhost } from "@/utils/platforms";
import createAuth0Client from "@auth0/auth0-spa-js";
import Vue from "vue";
import { schema } from "./constants";
import useSoundNotifications from "@/composables/useSoundNotifications";

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () => window.history.replaceState({}, document.title, window.location.pathname);

// eslint-disable-next-line init-declarations
let instance;

/** Returns the current instance of the SDK */
export const getInstance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({
    onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
    redirectUri = window.location.origin,
    ...options
}) => {
    if (instance) {
        return instance;
    }

    // The 'instance' is simply a Vue object
    instance = new Vue({
        data() {
            return {
                loading: true,
                isAuthenticated: false,
                user: {},
                auth0Client: null,
                popupOpen: false,
                error: null,
                customer: {},
            };
        },

        /** Use this lifecycle method to instantiate the SDK client */
        async created() {
            let domain = window.location.hostname;

            if (isMobile()) {
                domain = localStorage.getItem("mobileDomainOverride") ?? window.location.hostname;
            }

            // Don't try to login when site is on the mobile landing page
            if (domain.startsWith("mobile.")) {
                this.loading = false;
                return;
            }

            const customer = await axiosApiGateway
                .get(`${process.env.VUE_APP_API_GATEWAY.replace("v1", "v2")}/customers/domain/${domain}/org-id`)
                .then((customerResponse) => customerResponse.data);

            if (!customer || !customer.orgId) {
                localStorage.removeItem("mobileDomainOverride");
                return router.push("error-404");
            }

            const overwriteToken = Vue.prototype.$cookies.get("overwriteToken");

            // Create a new instance of the SDK client using members of the given options object
            this.auth0Client = overwriteToken
                ? new MockAuth0Client(overwriteToken)
                : await createAuth0Client({
                      ...options,
                      domain: process.env.VUE_APP_AUTH0_DOMAIN,
                      client_id: isPwa()
                          ? process.env.VUE_APP_AUTH0_MOBILE_CLIENT_ID
                          : process.env.VUE_APP_AUTH0_CLIENT_ID,
                      redirect_uri: redirectUri,
                      organization: customer.orgId,
                  });

            const urlParams = new URLSearchParams(window.location.search);

            if (urlParams.has("error")) {
                await this.handleLoginError(urlParams);
                return;
            }

            if (urlParams.has("invitation") && urlParams.has("organization")) {
                await this.handleLoginWithInvite();
                return;
            }

            // If the user is returning to the app after authentication..
            if (urlParams.has("code") && urlParams.has("state")) {
                await this.handleLoginWithStateCode();
            }

            if (overwriteToken) {
                await this.handleLoginOverwrite();
            }

            this.isAuthenticated = await this.auth0Client.isAuthenticated();
            this.loading = false;
        },
        methods: {
            async handleLoginError(urlParams) {
                if (urlParams.get("error_description") === "login_expired") {
                    await this.logout();
                    return;
                }
                if (urlParams.get("error_description") === "user is blocked") {
                    // I don't use the router because it create a bug the component will not be rendered.
                    window.location = window.location.origin + "/user-blocked";
                } else {
                    window.location = window.location.origin + "/not-authorized";
                }
            },
            async handleLoginOverwrite() {
                if (localStorage.getItem("userData")) {
                    return;
                }

                const { id_token } = await this.auth0Client.getTokenSilently();

                return this.setUser(id_token);
            },
            async handleLoginWithInvite() {
                return this.loginOrRedirect({
                    organization: urlParams.get("organization"),
                    invitation: urlParams.get("invitation"),
                }).then(() => {
                    this.user.decryptionKey = this.customer.decryptionKey;
                    this.user.storageName = this.customer.storageName;

                    localStorage.setItem("userData", JSON.stringify(this.user));

                    const { getUserSetting } = useUserSettings();

                    store.commit("appConfig/UPDATE_USER_SETTINGS", {
                        timeNotation: getUserSetting("timeNotation", 24),
                        language: getUserSetting("language", "en"),
                        enterToSend: getUserSetting("enterToSend", "1"),
                        smartInbox: getUserSetting("smartInboxEnabled", "1"),
                    });

                    // ** SIGNED COOKIES ** For AWS S3 bucket policies
                    // https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/private-content-signed-cookies.html#private-content-signed-cookie-misuse
                    useAws.getSignedCookies(this.customer.storageName).then((signedCookies) => {
                        const { setSignedCookies } = useCookies();
                        setSignedCookies(signedCookies);
                    });
                });
            },
            async handleLoginWithStateCode() {
                this.error = null;
                this.loading = true;

                await this.login(async () => {
                    // handle the redirect and retrieve tokens
                    return await this.auth0Client
                        .getTokenSilently({ detailedResponse: true })
                        .then((token) => token.id_token);
                }).then(() => {
                    this.user.decryptionKey = this.customer.decryptionKey;
                    this.user.storageName = this.customer.storageName;

                    localStorage.setItem("userData", JSON.stringify(this.user));

                    const { getUserSetting } = useUserSettings();
                    store.commit("appConfig/UPDATE_USER_SETTINGS", {
                        timeNotation: getUserSetting("timeNotation", 24),
                        language: getUserSetting("language", "en"),
                        enterToSend: getUserSetting("enterToSend", "1"),
                        smartInbox: getUserSetting("smartInboxEnabled", "1"),
                    });

                    // ** SIGNED COOKIES ** For AWS S3 bucket policies
                    // https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/private-content-signed-cookies.html#private-content-signed-cookie-misuse
                    useAws.getSignedCookies(this.customer.storageName).then((signedCookies) => {
                        const { setSignedCookies } = useCookies();
                        setSignedCookies(signedCookies);
                    });
                });

                const { appState } = await this.auth0Client.handleRedirectCallback();
                onRedirectCallback(appState);
            },

            getUserAbilities(idToken = null) {
                const headers = {};

                if (idToken) {
                    headers.Authorization = `Bearer ${idToken}`;
                }

                // DENNIS 2022-08: Why do we take the Axios v1 instance and replace the URL from v1 to v2? Use Axios v2 instance directly.
                return axiosApiGateway
                    .get(`${process.env.VUE_APP_API_GATEWAY.replace("v1", "v2")}/auth/permissions`, {
                        headers,
                    })
                    .then((permissionsResponse) => permissionsResponse.data);
            },

            async setUser(idToken) {
                this.user = await this.auth0Client.getUser();

                let [user, customer] = await Promise.all([
                    axios.get(`/users/${this.user[schema.userId]}`, {
                        params: {
                            fields: "displayName,email,name,role,status,lastLogin,pictureUrl",
                        },
                        headers: {
                            Authorization: `bearer ${idToken}`,
                        },
                    }),
                    axios.get(
                        `${process.env.VUE_APP_API_GATEWAY.replace("v1", "v2")}/customers/${
                            this.user[schema.customerId]
                        }/`,
                        {
                            params: {
                                fields: "storageName,encryptionKey,decryptionKey",
                            },
                            headers: {
                                Authorization: `bearer ${idToken}`,
                            },
                        }
                    ),
                ]).then((responses) => responses.map((response) => response.data));

                const userUpdates = {};

                if (!user?.lastLogin && !user?.active) {
                    userUpdates.active = 1;
                }

                userUpdates.lastLogin = new Date().toISOString();
                userUpdates.status = "available";
                userUpdates.auth0Id = this.user.sub;
                axios
                    .patch(`/users/${this.user[schema.userId]}`, userUpdates, {
                        headers: {
                            Authorization: `bearer ${idToken}`,
                        },
                    })
                    .catch();

                user = {
                    ...user,
                    ...userUpdates,
                };

                // User can have multiple roles, this is supported on most other places, the singular role can be used as fallback
                user.roles = this.user[schema.roles];
                user.role = user.roles[0];

                user.ability = await this.getUserAbilities(idToken);

                this.$ability.update(user.ability);
                this.user = user;
                this.customer = customer;

                localStorage.setItem("userData", JSON.stringify(user));

                const { getUserSetting } = useUserSettings();

                const { smartInbox, timeNotation, language, enterToSend } = useAppConfig();
                smartInbox.value = getUserSetting("smartInboxEnabled", "0");
                language.value = getUserSetting("language", "en");
                enterToSend.value = getUserSetting("enterToSend", "1");
                timeNotation.value = getUserSetting("timeNotation", 24);

                // --- SET THE WEB NOTIFICATION SETTINGS INTO THE STATE
                const {
                    notifyIncomingMessage,
                    notifyIncomingMessageNew,
                    notifyConversationUnassigned,
                    notifyConversationAssigned,
                    notifyTeamchatStarted,
                    notifyTeamchatMessage,
                } = useWebNotifications();

                notifyIncomingMessage.value = getUserSetting("notifyIncomingMessage", false);
                notifyIncomingMessageNew.value = getUserSetting("notifyIncomingMessageNew", false);
                notifyConversationUnassigned.value = getUserSetting("notifyConversationUnassigned", false);
                notifyConversationAssigned.value = getUserSetting("notifyConversationAssigned", false);
                notifyTeamchatStarted.value = getUserSetting("notifyTeamchatStarted", false);
                notifyTeamchatMessage.value = getUserSetting("notifyTeamchatMessage", false);

                // -- SET THE SOUND NOTIFICATIONS INTO THE STATE
                const {
                    soundNotifyIncomingMessage,
                    soundNotifyIncomingMessageNew,
                    soundNotifyConversationUnassigned,
                    soundNotifyConversationAssigned,
                    soundNotifyTeamchatStarted,
                    soundNotifyTeamchatMessage,
                } = useSoundNotifications();

                soundNotifyIncomingMessage.value = getUserSetting("soundNotifyIncomingMessage", false);
                soundNotifyIncomingMessageNew.value = getUserSetting("soundNotifyIncomingMessageNew", false);
                soundNotifyConversationUnassigned.value = getUserSetting("soundNotifyConversationUnassigned", false);
                soundNotifyConversationAssigned.value = getUserSetting("soundNotifyConversationAssigned", false);
                soundNotifyTeamchatStarted.value = getUserSetting("soundNotifyTeamchatStarted", false);
                soundNotifyTeamchatMessage.value = getUserSetting("soundNotifyTeamchatMessage", false);

                // -- SET THE WEBSOCKET CONNECTION
                this.$socket.client.io.opts.query = {
                    accessToken: idToken,
                };
                this.$socket.client.connect();
            },

            async login(getIdTokenMethod) {
                return getIdTokenMethod()
                    .then((idToken) => this.setUser(idToken))
                    .then(() => (this.error = null))
                    .catch((error) => {
                        this.error = error;
                    })
                    .finally(async () => {
                        this.isAuthenticated = await this.auth0Client.isAuthenticated();
                        if (this.isAuthenticated) {
                            // Put the online-status in the store, so it's persist after F5.
                            const { onlineStatus } = useOnlineStatus();
                            onlineStatus.value = this.user.status;
                            // Save logged in event
                            const { saveEvent } = useEvents();
                            saveEvent({ name: "user::logged_in" });
                        }
                    });
            },

            /** Handles the callback when logging in using a redirect */
            async handleRedirectCallback() {
                this.loading = true;
                await this.login(async () => {
                    await this.auth0Client.handleRedirectCallback();

                    const accessToken = await this.auth0Client.getTokenSilently({ detailedResponse: true });
                    return accessToken.id_token;
                });
                this.loading = false;
            },

            getUser() {
                return this.auth0Client.getUser();
            },
            /** Logs the user out and removes their session on the authorization server */
            logout(options) {
                if (!options || !options?.returnTo) {
                    options = {
                        ...options,
                        returnTo: redirectUri,
                    };
                }

                if (isMobile() && localStorage.getItem("mobileDomainOverride")) {
                    options.returnTo = isLocalhost()
                        ? "https://localhost.saysimple-test.nl:8080/mobile"
                        : `https://mobile.${getRootDomain()}`;
                }

                // Remove userData from localStorage
                localStorage.removeItem("userData");
                localStorage.removeItem("mobileDomainOverride");
                this.$ability.update(initialAbility);
                return this.auth0Client.logout(options);
            },

            async loginOrRedirect(options) {
                this.isAuthenticated = await this.auth0Client.isAuthenticated();

                if (!this.isAuthenticated) {
                    await this.auth0Client.loginWithRedirect(options);
                }

                return this.isAuthenticated;
            },

            async reloadPrivileges() {
                this.user.ability = await this.getUserAbilities();
                this.$ability.update(this.user.ability);
                localStorage.setItem("userData", JSON.stringify(this.user));
            },
        },
    });

    return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
    install(Vue, options) {
        Vue.prototype.$auth = useAuth0(options);
    },
};
